import {BrowserModule} from '@angular/platform-browser';
import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {httpInterceptorProviders} from './interceptors';

import {AppRoutingModule} from './app-routing.module';
import {MatDialogModule} from '@angular/material/dialog';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MatTreeModule} from '@angular/material/tree';
import {MatSortModule} from '@angular/material/sort';
import {MatIconModule} from '@angular/material/icon';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatStepperModule} from '@angular/material/stepper';
import {MatBadgeModule} from '@angular/material/badge';
import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatTableModule} from '@angular/material/table';
import {MatSliderModule} from '@angular/material/slider';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatInputModule} from '@angular/material/input';
import {MatListModule} from '@angular/material/list';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatMenuModule} from '@angular/material/menu';
import {MatTabsModule} from '@angular/material/tabs';
import {MatTooltipModule} from '@angular/material/tooltip';

import {AppComponent} from './app.component';
import {HeaderComponent} from './components/static-pages/header/header.component';
import {FooterComponent} from './components/static-pages/footer/footer.component';
import {VocabularyComponent} from './components/vocabulary/vocabulary.component';
import {LoginComponent} from './components/user/login/login.component';
import {EntryDetailsComponent} from './components/common/entry-details/entry-details.component';
import {NewWordComponent} from './components/vocabulary/create/new-word/new-word.component';
import {NewCategoryComponent} from './components/vocabulary/create/new-category/new-category.component';
import {RegistrationComponent} from './components/user/registration/registration.component';
import {ResetPasswordComponent} from './components/user/reset-password/reset-password.component';
import {ProfileComponent} from './components/user/profile/profile.component';
import {UserManagementComponent} from './components/user/user-management/user-management.component';
import {ManageUserComponent} from './components/user/manage-user/manage-user.component';
import {ImprintComponent} from './components/static-pages/imprint/imprint.component';
import {ConfirmationDialogComponent} from './components/common/confirmation-dialog/confirmation-dialog.component';
import {ChangePasswordComponent} from './components/user/change-password/change-password.component';
import {EditProfileComponent} from './components/user/edit-profile/edit-profile.component';
import {HomeComponent} from './components/static-pages/home/home.component';
import {ResetTokenComponent} from './components/user/reset-token/reset-token.component';
import {TextPatternComponent} from './components/text-pattern/text-pattern.component';
import {MatChipsModule} from '@angular/material/chips';
import {TextPatternDetailComponent} from './components/text-pattern/text-pattern-detail/text-pattern-detail.component';
import {SysGrammarComponent} from './components/grammar/sys-grammar/sys-grammar.component';
import {NewGrammarCategoryComponent} from './components/grammar/new-grammar-category/new-grammar-category.component';
import {NewGrammarEntryComponent} from "./components/grammar/new-grammar-entry/new-grammar-entry.component";
import {GrammarComponent} from "./components/grammar/grammar.component";
import {SimpleSelectComponent} from "./components/select/simple-select/simple-select.component";
import {GrammarOverviewComponent} from "./components/grammar/grammar-overview/grammar-overview.component";
import {FuncGrammarComponent} from "./components/grammar/func-grammar/func-grammar.component";
import {CategoryTreeComponent} from "./components/common/category-tree/category-tree.component";
import {TableComponent} from "./components/common/table/table.component";
import {ReferenceTableComponent} from "./components/common/reference-table/reference-table.component";
import {ReferenceSelectComponent} from "./components/select/reference-select/reference-select.component";
import {SelectDialogComponent} from "./components/select/select-dialog/select-dialog.component";
import {SearchBarComponent} from "./components/common/search-bar/search-bar.component";
import {TextTypeComponent} from "./components/text-type/text-type.component";
import {NewTextTypeComponent} from "./components/text-type/new-text-type/new-text-type.component";
import {MarkdownEditorComponent} from "./components/common/markdown-editor/markdown-editor.component";
import {VerbalActionComponent} from "./components/verbal-action/verbal-action.component";
import {VerbalActionDetailComponent} from "./components/verbal-action/verbal-action-detail/verbal-action-detail.component";
import {CanDoStatementComponent} from "./components/can-do-statement/can-do-statement.component";
import {CanDoStatementPanelComponent} from "./components/can-do-statement/can-do-statement-panel/can-do-statement-panel.component";
import {NewCanDoStatementComponent} from "./components/can-do-statement/create/new-can-do-statement/new-can-do-statement.component";
import {NewCanDoStatementCategoryComponent} from "./components/can-do-statement/create/new-category/new-can-do-statement-category.component";
import {HeaderSearchComponent} from './components/static-pages/header/header-search/header-search.component';
import {SearchResultItemComponent} from './components/common/search-result-item/search-result-item.component';
import {SearchResultsComponent} from './components/search-results/search-results.component';
import {FolderOverviewComponent} from './components/folders/folder-overview/folder-overview.component';
import {FolderDetailComponent} from './components/folders/folder-detail/folder-detail.component';
import {FolderEntryTableComponent} from './components/folders/folder-entry-table/folder-entry-table.component';
import {FolderNameDialogComponent} from './components/folders/folder-name-dialog/folder-name-dialog.component';
import {FolderShareDialogComponent} from './components/folders/folder-share-dialog/folder-share-dialog.component';
import {FolderExportDialogComponent} from './components/folders/folder-export-dialog/folder-export-dialog.component';
import {AddToFolderDialogComponent} from './components/folders/add-to-folder-dialog/add-to-folder-dialog.component';
import {FolderUsageHintComponent} from './components/folders/folder-usage-hint/folder-usage-hint.component';
import {NotificationsComponent} from './components/notifications/notifications.component';
import {SettingsDialogComponent} from './components/user/settings-dialog/settings-dialog.component';
import {DeleteAccountDialogComponent} from './components/user/delete-account-dialog/delete-account-dialog.component';

@NgModule({
  bootstrap: [AppComponent],
  imports: [BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    BrowserAnimationsModule,
    MatTreeModule,
    MatTableModule,
    MatSliderModule,
    MatSortModule,
    MatCheckboxModule,
    MatIconModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatToolbarModule,
    MatButtonToggleModule,
    MatGridListModule,
    MatCardModule,
    MatListModule,
    MatExpansionModule,
    MatRadioModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatPaginatorModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatStepperModule,
    MatSnackBarModule,
    MatMenuModule,
    MatTabsModule,
    MatTooltipModule,
    MatBadgeModule,
    MatChipsModule,
    MarkdownEditorComponent,
    FolderUsageHintComponent
  ],
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    VocabularyComponent,
    LoginComponent,
    EntryDetailsComponent,
    NewWordComponent,
    NewCategoryComponent,
    RegistrationComponent,
    ResetPasswordComponent,
    ProfileComponent,
    UserManagementComponent,
    ManageUserComponent,
    ImprintComponent,
    ConfirmationDialogComponent,
    ChangePasswordComponent,
    EditProfileComponent,
    HomeComponent,
    ResetTokenComponent,
    TextPatternComponent,
    TextPatternDetailComponent,
    SimpleSelectComponent,
    NewGrammarCategoryComponent,
    GrammarComponent,
    SysGrammarComponent,
    NewGrammarEntryComponent,
    GrammarOverviewComponent,
    FuncGrammarComponent,
    CategoryTreeComponent,
    TableComponent,
    SearchBarComponent,
    ReferenceTableComponent,
    ReferenceSelectComponent,
    SelectDialogComponent,
    TextTypeComponent,
    NewTextTypeComponent,
    VerbalActionComponent,
    VerbalActionDetailComponent,
    CanDoStatementComponent,
    CanDoStatementPanelComponent,
    NewCanDoStatementComponent,
    NewCanDoStatementCategoryComponent,
    HeaderSearchComponent,
    SearchResultItemComponent,
    SearchResultsComponent,
    FolderOverviewComponent,
    FolderDetailComponent,
    FolderEntryTableComponent,
    FolderNameDialogComponent,
    FolderShareDialogComponent,
    FolderExportDialogComponent,
    AddToFolderDialogComponent,
    NotificationsComponent,
    SettingsDialogComponent,
    DeleteAccountDialogComponent,
  ],
  providers: [
    httpInterceptorProviders,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {

}
